import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import './LandingPage.css'; // Put all your CSS styles here
import SimpleAudioManager from '../utils/SimpleAudioManager';

function LandingPage() {
  const navigate = useNavigate();
  const { user, profile, isAuthenticated } = useAuth();
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const digitalRainRef = useRef(null);
  
  useEffect(() => {
    // Check if cookie consent was previously given
    if (!localStorage.getItem('cookieConsent')) {
      setTimeout(() => {
        setShowCookieBanner(true);
      }, 1500);
    }
    
    // Create digital rain effect
    createDigitalRain();
    
    // Check user's audio preference
    const audioEnabled = localStorage.getItem('audio_enabled') !== 'false';
    
    // Preload and play background music if enabled
    SimpleAudioManager.preloadAudio('bgm', '/background-music.mp3')
      .then(() => {
        if (audioEnabled) {
          SimpleAudioManager.play('bgm', {
            loop: true,
            volume: 0.3
          });
          setAudioPlaying(true);
        }
      })
      .catch(err => {

      });
    
    // Cleanup function
    return () => {
      // Stop and clean up audio when component unmounts
      SimpleAudioManager.stop('bgm');
      
      // Clean up digital rain
      if (digitalRainRef.current) {
        const container = document.getElementById('digital-rain');
        if (container) {
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
        }
        digitalRainRef.current = null;
      }
    };
  }, []);
  
  function createDigitalRain() {
    // Your existing digital rain code, adapted for React
    if (digitalRainRef.current) return; // Already created
    
    const container = document.getElementById('digital-rain');
    if (!container) return;
    
    // Create digital rain columns (this is a placeholder - use your actual implementation)
    const columns = 50;
    const characters = "01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";
    
    for (let i = 0; i < columns; i++) {
      const column = document.createElement('div');
      column.className = 'rain-character';
      column.style.left = `${Math.floor(Math.random() * 100)}%`;
      column.style.animationDuration = `${5 + Math.random() * 10}s`;
      column.textContent = characters.charAt(Math.floor(Math.random() * characters.length));
      container.appendChild(column);
    }
    
    digitalRainRef.current = true;
  }
  
  function toggleAudio() {
    const newAudioState = !audioPlaying;
    setAudioPlaying(newAudioState);
    localStorage.setItem('audio_enabled', newAudioState.toString());
    
    if (newAudioState) {
      // Resume or start music
      SimpleAudioManager.resume('bgm');
      
      // If not already playing, start it
      const audio = SimpleAudioManager.getAudio('bgm');
      if (!audio || (audio && audio.paused)) {
        SimpleAudioManager.play('bgm', {
          loop: true,
          volume: 0.3
        });
      }
    } else {
      // Pause music
      SimpleAudioManager.pause('bgm');
    }
  }
  
  function handleCookieAccept() {
    localStorage.setItem('cookieConsent', 'true');
    setShowCookieBanner(false);
  }
  
  function startGame() {
    // Play a button sound if we have one
    const buttonSound = SimpleAudioManager.getAudio('button');
    if (buttonSound && audioPlaying) {
      SimpleAudioManager.play('button');
    }
    
    // Navigate to game component
    navigate('/game');
  }

  return (
    <div id="landing-page" className="relative w-full h-screen cyber-grid" style={{overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center"}}>
      {/* Your existing landing page HTML, converted to JSX */}
      <div className="digital-rain" id="digital-rain"></div>
      
      <div className="relative z-10 flex flex-col items-center justify-center text-center max-w-6xl mx-auto p-8 bg-gray-900 bg-opacity-80 rounded-xl border border-blue-500/30 backdrop-blur-sm" 
           style={{position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "90%", maxWidth: "1100px", maxHeight: "90vh", overflowY: "auto", overflowX: "hidden", marginLeft: "auto", marginRight: "auto"}}>
        {/* Game title and content */}
        <div className="title-container">
          <h1 className="title-text">THE SECURITY ONION</h1>
        </div>
        <div className="subtitle-container">
          <h2 className="subtitle-text">LAYER DEFENDER</h2>
        </div>
        
        {/* Game description */}
        <div className="game-description">
          <h2>PROTECT THE LAYERS. SECURE THE SYSTEM.</h2>
          <p>The network's security layers have been compromised! As an elite cyber security specialist, you must defend each layer of the Security Onion before the entire infrastructure falls to malicious attacks.</p>
          
          {/* Show authentication status */}
          {isAuthenticated && profile ? (
            <div className="mb-4 p-4 bg-green-900/30 border border-green-700 rounded-lg">
              <h3 className="font-semibold text-green-400 mb-1">Welcome back, {profile.username}!</h3>
              <p className="text-sm text-gray-300">
                You're logged in with your Security Onion account. Your scores will be automatically saved to the global leaderboard.
              </p>
            </div>
          ) : (
            <div className="mb-4 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
              <h3 className="font-semibold text-blue-400 mb-1">Play as Guest</h3>
              <p className="text-sm text-gray-300">
                You're playing as a guest. To save your scores to the leaderboard, please log in through your Security Onion account.
              </p>
            </div>
          )}
          
          {/* More description here */}
        </div>
        
        {/* Start button */}
        <div style={{display: "flex", justifyContent: "center", width: "100%", marginTop: "1rem", alignItems: "center", textAlign: "center"}}>
          <button onClick={startGame} className="pulse-button" 
                  style={{backgroundColor: "#2563eb", color: "white", fontWeight: "bold", padding: "0.85rem 2.5rem", borderRadius: "0.5rem", fontSize: "1.2rem", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", maxWidth: "90%", margin: "0 auto"}}>
            START MISSION
          </button>
        </div>
        
        <div className="mt-8 text-blue-400 text-sm text-center">v1.0.2</div>
      </div>
      
      {/* Cookie banner */}
      {showCookieBanner && (
        <div id="cookie-banner" className="cookie-banner show">
          <p><strong>Privacy Notice</strong></p>
          <p>This game doesn't use cookies or collect any personal data. Your gameplay information stays on your device.</p>
          <button onClick={handleCookieAccept} className="cookie-btn">Got it</button>
        </div>
      )}
      
      {/* Sound control */}
      <div className="sound-control" onClick={toggleAudio}>
        {audioPlaying ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M19.07 4.93C20.9447 6.80528 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 9L16 15" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 9L22 15" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    </div>
  );
}

export default LandingPage;